import { Action, createReducer, on } from '@ngrx/store';

import { IProviderState, IUserState } from '../models/state.model';
import * as coreActions from './core.actions';
import { capitalizeFirstLastName } from '../../utils/capitalizeFirstLastName';

const initialUserState: IUserState = {
  user: undefined,
  refreshToken: {
    isRefreshInProgress: false,
    url: undefined,
    resetInactivityTime: undefined
  },
  myOaas: undefined,
  idleTimeoutResetCount: 0,
  shouldDisableUserMenu: false
};

const initialProviderState: IProviderState = {
  favoriteProviders: [],
  clearProviderSearchResults: false
};

const reducer = createReducer(
  initialUserState,
  on(coreActions.actionSignInUser, (state, { user }) => ({
    ...state,
    user: {
      firstName: user?.profile?.given_name,
      lastName: user?.profile?.family_name,
      username: user?.profile?.preferred_username,
      providerType: null
    }
  })),
  on(coreActions.actionGetPOLUserProfileSuccess, (state, { profile }) => ({
    ...state,
    user: {
      ...state.user,
      providerType: profile?.userProfile?.providerType,
      useAgreement: profile?.useAgreement
    }
  })),
  on(coreActions.actionLoadUserFromSessionStorage, (state, { user }) => ({
    ...state,
    user: {
      firstName: user?.given_name,
      lastName: user?.family_name,
      username: user?.preferred_username,
      providerType: user?.providerType
    }
  })),
  on(coreActions.actionGetUserPreferencesSuccess, (state, { user }) => ({
    ...state,
    user: { ...state.user, ...user }
  })),
  on(coreActions.actionRefreshToken, (state, { refreshToken }) => ({
    ...state,
    refreshToken
  })),
  on(coreActions.resetInactivityTime, (state) => ({
    ...state,
    refreshToken: { ...state.refreshToken, resetInactivityTime: false }
  })),
  on(coreActions.actionSignOutUser, () => ({
    ...initialUserState
  })),
  on(coreActions.actionUpdateUserPreferences, (state) => ({
    ...state,
    isLoading: true,
    isError: false,
    error: null
  })),
  on(coreActions.actionUpdateUserPreferencesSuccess, (state, { user }) => ({
    ...state,
    user: { ...state.user, ...user },
    isLoading: false,
    isError: false,
    error: null
  })),
  on(coreActions.actionUpdateUserPreferencesFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    isError: true,
    error
  })),
  on(coreActions.actionGetMyOaas, (state) => ({
    ...state,
    isLoading: true,
    isError: false,
    error: null
  })),
  on(coreActions.actionGetMyOaasSuccess, (state, { myOaas }) => ({
    ...state,
    isLoading: false,
    isError: false,
    error: null,
    myOaas: {
      oaaUsers: myOaas.oaaUsers.map(capitalizeFirstLastName),
      securityGroups: myOaas.securityGroups
    }
  })),
  on(coreActions.actionGetMyOaasFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    isError: true,
    error
  })),
  on(coreActions.resetIdleTimeout, state => ({
    ...state,
    idleTimeoutResetCount: state.idleTimeoutResetCount + 1
  })),
  on(coreActions.disableUserMenu, (state, { shouldDisable }) => ({
    ...state,
    shouldDisableUserMenu: shouldDisable
  }))
);

const providerReducer = createReducer(
  initialProviderState,
  on(coreActions.actionSetFavoriteProviders, (state, { favoriteProviders }) => ({
    ...state,
    favoriteProviders: favoriteProviders
  })),
  on(coreActions.actionAddFavoriteProviderSuccess, (state, { favoriteProvider }) => ({
    ...state,
    favoriteProviders: [...state.favoriteProviders, favoriteProvider]
  })),
  on(coreActions.actionRemoveFavoriteProviderSuccess, (state, { favoriteProvider }) => ({
    ...state,
    favoriteProviders: [
      ...state.favoriteProviders.filter((provider) => provider.providerId !== favoriteProvider.providerId)
    ]
  })),
  on(coreActions.getFavoriteProviders, (state) => ({
    ...state,
    favoriteProviders: null,
    loading: true,
    error: null
  })),
  on(coreActions.getFavoriteProvidersSuccess, (state, { favoriteProviders }) => ({
    ...state,
    favoriteProviders: favoriteProviders?.results,
    loading: false,
    error: null
  })),
  on(coreActions.getFavoriteProvidersFailure, (state, { error }) => ({
    ...state,
    favoriteProviders: null,
    loading: false,
    error
  })),
  on(coreActions.addFavoriteProviderSuccess, (state, { favoriteProviders }) => ({
    ...state,
    favoriteProviders: favoriteProviders?.results
  })),
  on(coreActions.removeFavoriteProviderSuccess, (state, { favoriteProviders }) => ({
    ...state,
    favoriteProviders: favoriteProviders?.results
  })),
  on(coreActions.addFavoriteProviderError, (state, { error }) => ({
    ...state,
    error
  })),
  on(coreActions.removeFavoriteProviderError, (state, { error }) => ({
    ...state,
    error
  })),
  on(coreActions.resetFavoriteProviders, (state) => ({
    ...state,
    favoriteProviders: [],
    selectedProvider: null
  })),
  on(coreActions.resetFavoriteProvidersSearch, (state) => ({
    ...state,
    selectedProvider: null,
    providerSearchResults: [],
    clearProviderSearchResults: true
  })),
  on(coreActions.resetProviderSearchResults, (state) => ({
    ...state,
    clearProviderSearchResults: false
  })),
  on(coreActions.setSelectedProvider, (state, { selectedProvider }) => ({
    ...state,
    selectedProvider
  })),
  on(coreActions.setProviderSearchResults, (state, { providerSearchResults }) => ({
    ...state,
    providerSearchResults
  }))
);

export function userStateReducer(state: IUserState | undefined, action: Action): IUserState {
  return reducer(state, action);
}

export function providerStateReducer(state: IProviderState | undefined, action: Action): IProviderState {
  return providerReducer(state, action);
}
